/**
 * Preferências de perfil guardadas localmente (avatar, telefone, fuso).
 * O avatar em data URL / https persiste; URLs `blob:` não — são descartadas ao ler.
 */
export const USER_PROFILE_PREFERENCES_KEY = 'taskeo.user.profile.preferences';

export interface UserProfilePreferences {
  avatarUrl: string;
  phoneCountryIso2: string;
  phone: string;
  useSameWhatsApp: boolean;
  whatsappCountryIso2: string;
  whatsapp: string;
  timezone: string;
  /** pt-BR | en-US — alinhado ao perfil na API quando existir. */
  language?: string;
}

export const DEFAULT_USER_PROFILE_PREFERENCES: UserProfilePreferences = {
  avatarUrl: '',
  phoneCountryIso2: 'BR',
  phone: '',
  useSameWhatsApp: true,
  whatsappCountryIso2: 'BR',
  whatsapp: '',
  timezone: '',
  language: 'pt-BR',
};

export function sanitizePersistedAvatarUrl(url: string | undefined | null): string {
  const t = (url ?? '').trim();
  if (!t) return '';
  if (t.toLowerCase().startsWith('blob:')) return '';
  return t;
}

export function readUserProfilePreferences(): UserProfilePreferences | null {
  try {
    const raw = localStorage.getItem(USER_PROFILE_PREFERENCES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserProfilePreferences;
    const avatarUrl = sanitizePersistedAvatarUrl(parsed.avatarUrl);
    if (avatarUrl !== (parsed.avatarUrl ?? '').trim()) {
      writeUserProfilePreferences({ ...parsed, avatarUrl });
    }
    return { ...parsed, avatarUrl };
  } catch {
    return null;
  }
}

export function writeUserProfilePreferences(value: UserProfilePreferences): void {
  try {
    const avatarUrl = sanitizePersistedAvatarUrl(value.avatarUrl);
    localStorage.setItem(USER_PROFILE_PREFERENCES_KEY, JSON.stringify({ ...value, avatarUrl }));
  } catch {
    // quota excedida, etc.
  }
}

/** Atualiza só o avatar no JSON guardado (mantém restantes chaves como estão). */
export function patchUserProfilePreferencesAvatar(avatarUrl: string): void {
  const prev = readUserProfilePreferences();
  const base = prev ?? DEFAULT_USER_PROFILE_PREFERENCES;
  writeUserProfilePreferences({
    ...base,
    avatarUrl: sanitizePersistedAvatarUrl(avatarUrl),
  });
}

/** Remove as preferências locais — usado no logout e na troca de conta no mesmo dispositivo. */
export function clearUserProfilePreferences(): void {
  try {
    localStorage.removeItem(USER_PROFILE_PREFERENCES_KEY);
  } catch {
    // storage indisponível
  }
}
